import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const IMG = (u) => `${u}&w=1100&q=80&auto=format&fit=crop`;

const COMPANIES = [
  {
    name: "Naehas", tag: "Financial Services", url: "https://www.naehas.com/", shot: "/shots/naehas.jpg", logo: "/logos/naehas.png",
    desc: "Compliant marketing and disclosures at enterprise scale.",
    image: IMG("https://images.unsplash.com/photo-1576831356777-59f382e176b4?crop=entropy&cs=srgb&fm=jpg"),
    story: [
      "Naehas began with an operator who had spent decades inside regulated banking \u2014 someone who knew exactly why compliant marketing was slow, brittle, and expensive.",
      "They brought the problem and the relationships. Govil Ventures brought the founding team, the capital, and an operating company already running. On day one, the work was product \u2014 not paperwork.",
    ],
  },
  {
    name: "Zenarate", tag: "Real Estate \u00b7 AI", url: "https://www.zenarate.com/", shot: "/shots/zenarate.jpg", logo: "/logos/zenarate.png",
    desc: "AI-generated feasibility for real estate development.",
    image: IMG("https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg"),
    story: [
      "A veteran of real estate development had watched countless projects die in weeks of manual feasibility studies. The opportunity was clear; the starting line was not.",
      "We assembled engineers and designers around their judgment, and Zenarate shipped AI-driven feasibility in a fraction of the time \u2014 built this way from the first line of code.",
    ],
  },
  {
    name: "Zenafide", tag: "Trust \u00b7 Identity", url: "https://zenafide.com/", shot: "/shots/zenafide.jpg", logo: "/logos/zenafide.png",
    desc: "Verification infrastructure for high-stakes transactions.",
    image: IMG("https://images.unsplash.com/photo-1531591022136-eb8b0da1e6d0?crop=entropy&cs=srgb&fm=jpg"),
    story: [
      "Trust breaks quietly, in the seams between systems. Zenafide's founder had spent a career watching those seams \u2014 and knew precisely where verification failed.",
      "With the platform, capital and partnership already in place, they turned that insight into infrastructure others now build on.",
    ],
  },
  {
    name: "Lexafide", tag: "Legal Technology", url: "https://www.lexafide.com/", logo: "/logos/lexafide.png", shot: "/shots/lexafide.jpg",
    desc: "Autonomous contract and compliance operations.",
    image: IMG("https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg"),
    story: [
      "Legal operations reward experience and punish everyone else. Lexafide's founder had lived inside enterprise legal long enough to name every bottleneck.",
      "Govil Ventures provided the operating company and technology repository; the founder provided the distribution. Lexafide was in market before most teams finish hiring.",
    ],
  },
  {
    name: "Stellaforce", tag: "Enterprise \u00b7 Workforce", url: "https://www.stellaforce.com/", shot: "/shots/stellaforce.jpg", logo: "/logos/stellaforce.png",
    desc: "Workforce intelligence for mission-critical teams.",
    image: "https://images.pexels.com/photos/35972898/pexels-photo-35972898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1100",
    story: [
      "The best workforce decisions come from people who've made the hard ones. Stellaforce started with an operator who had built and scaled critical teams for years.",
      "They brought the two things we can't buy. We'd already built the other four. Stellaforce is the company that combination makes possible.",
    ],
  },
];

function CompanyCard({ c, index }) {
  return (
    <motion.a
      data-testid={`portfolio-card-${index + 1}`}
      href={c.url || undefined}
      target={c.url ? "_blank" : undefined}
      rel={c.url ? "noopener noreferrer" : undefined}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.8, ease: EASE, delay: (index % 3) * 0.08 }}
      className="group relative flex flex-col overflow-hidden rounded-sm border border-white/10 bg-elevated text-left transition-[transform,border-color,box-shadow] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-2 hover:border-gold/60 hover:shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
    >
      {c.shot ? (
        <div className="relative aspect-[16/10] overflow-hidden bg-noir">
          <img
            src={c.shot}
            alt={`${c.name} website`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        </div>
      ) : (
        <div className="relative flex aspect-[16/10] items-center justify-center overflow-hidden bg-white p-10">
          {c.logo ? (
            <img
              src={c.logo}
              alt={`${c.name} logo`}
              loading="lazy"
              decoding="async"
              className="h-10 w-auto max-w-[70%] object-contain transition-transform duration-500 group-hover:scale-[1.04] md:h-12"
            />
          ) : (
            <span className="text-center font-serif text-4xl font-bold tracking-tight text-noir transition-transform duration-500 group-hover:scale-[1.04] md:text-5xl">
              {c.name}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-1 flex-col justify-between gap-6 p-6">
        <div className="flex items-center justify-between gap-4">
          {c.logo ? (
            <img
              src={c.logo}
              alt={`${c.name} logo`}
              loading="lazy"
              decoding="async"
              className="max-h-6 w-auto max-w-[42%] object-contain object-left opacity-95 [filter:brightness(0)_invert(1)] md:max-h-7"
            />
          ) : (
            <span aria-hidden />
          )}
          <span className="inline-flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-gold transition-all duration-300 group-hover:gap-2.5">
            Visit website <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </motion.a>
  );
}

export default function Portfolio() {
  return (
    <section
      id="portfolio" data-testid="portfolio-section"
      className="relative scroll-mt-24 border-t border-white/10 bg-noir py-24 md:py-32 lg:py-40"
    >
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
          className="mb-6 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-gold"
        >
          04 &mdash; Portfolio
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE, delay: 0.05 }}
          className="mb-16 max-w-4xl font-serif leading-[1.02] tracking-tight text-cream"
          style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
        >
          Companies built this way.
        </motion.h2>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {COMPANIES.map((c, i) => (
            <CompanyCard key={c.name} c={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
